<template>
    <div
        class="ds-quick-bet-card"
        ref="cardRoot"
        :class="[
            { 'ds-quick-bet-card--success': isSuccess },
            { 'ds-quick-bet-card--white': isWhiteCard },
            !!lottery ? `ds-quick-bet-card--${lottery}` : '',
            { 'ds-quick-bet-card--divider-hidden': hideDivider },
            { 'ds-quick-bet-card--compressed': compressed },
        ]"
        :data-test="dataTestValue ? `quick-bet-${dataTestValue}` : null"
    >
        <video
            v-if="isSuccess && showVideo"
            alt="kulicnik loop animation"
            id="bet-success-loop"
            constrols="false"
            playsinline
            autoplay
            preload="auto"
            class="ds-quick-bet-card__animation"
            loop
            muted
            :poster="getFallbackPoster"
        >
            <source :src="getFallbackVideo" type="video/webm" />
        </video>

        <DsSmileStatic
            v-else-if="showSmile"
            class="ds-quick-bet-card__smile"
            :responsive="false"
            :face-position="FacePosition.LeftDown"
            :face-type="FaceType.BigSmile"
            shadow-color="black"
        />

        <div class="ds-quick-bet-card__header" :class="{ 'js-ds-quick-bet-card-header': !isSuccess }">
            <template v-if="showLotteryIcon">
                <div class="ds-quick-bet-card__lottery-icon-wrapper">
                    <div
                        class="ds-quick-bet-card__lottery-icon"
                        :class="{ 'ds-quick-bet-card__lottery-icon--default': isFallbackLotteryIcon }"
                    >
                        <WebpImage
                            class="ds-quick-bet-card__lottery-icon-picture"
                            css-class="ds-quick-bet-card__lottery-icon-img"
                            :src="isFallbackLotteryIcon ? getFallbackDefaultIcon : getFallbackIcon"
                            :alt="`${lottery} icon`"
                            width="24"
                            height="24"
                            :is-in-storybook="isInStorybook"
                        />
                    </div>
                    <p
                        v-if="heading"
                        class="ds-quick-bet-card__heading"
                        v-html="heading"
                        data-test="quick-bet-title"
                    ></p>
                </div>
            </template>
            <template v-else>
                <p v-if="heading" class="ds-quick-bet-card__heading" v-html="heading" data-test="quick-bet-title"></p>
            </template>
            <p v-if="subheading" class="ds-quick-bet-card__subheading" v-html="subheading"></p>
        </div>

        <hr v-if="!hideDivider && !isSuccess" class="ds-quick-bet-card__divider ds-divider" />

        <div class="ds-quick-bet-card__body">
            <slot></slot>
        </div>
        <slot name="button"></slot>
    </div>
</template>
<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";
import { initQuickBetCardLists } from "../quick-bet-card/ds-quick-bet-card";
import DsSmileStatic from "../smiles/ds-smile-static.vue";
import {
    DsSmileStaticFacePosition as FacePosition,
    DsSmileStaticFaceType as FaceType,
} from "../smiles/ds-smile-static-types";
import WebpImage from "../image/webp-image.vue";

// lottery needs to be set to string, as we need to create multisazneka variant,
// and enum Lottery doesn't have multisazenka as key value pair

const props = withDefaults(
    defineProps<{
        /**
         * Set heading
         */
        heading?: string;
        /**
         * Set subheading
         */
        subheading?: string;
        /**
         * Show white card
         */
        isWhiteCard?: boolean;
        /**
         * Show sucess card
         */
        isSuccess?: boolean;
        /**
         * Set lottery type
         */
        lottery?: string;
        /**
         * Use different asset path for SB
         */
        isInStorybook?: boolean;
        /**
         * Index of card in list used in data-test
         */
        dataTestValue?: string;
        /**
         * Show video loop
         */
        showVideo?: boolean;
        /**
         * Show smile
         */
        showSmile?: boolean;
        /**
         * Enable showing / hiding divider
         */
        hideDivider?: boolean;
        /**
         * Show lottery icon
         */
        showLotteryIcon?: boolean;
        /**
         * Use fallback icon
         */
        isFallbackLotteryIcon?: boolean;
        /**
         * Use compressed variant
         */
        compressed?: boolean;
    }>(),
    {
        isSuccess: false,
        isWhiteCard: false,
        showVideo: false,
        showSmile: false,
        hideDivider: false,
        isInStorybook: false,
        isFallbackLotteryIcon: false,
    }
);

const cardRoot = ref<HTMLElement>();

onMounted(() => {
    initQuickBetCardLists(cardRoot.value);
});

const getFallbackPoster = computed(() => {
    if (props.isInStorybook) return `/images/img/lotteries/kulicnik-bet-fallback.png`;
    return `/frontend/web/img/lotteries/ticket-check/kulicnik-bet-fallback.png`;
});

const getFallbackVideo = computed(() => {
    if (props.isInStorybook) return `/images/img/lotteries/kulicnik-bet.webm`;
    return `/frontend/web/img/lotteries/ticket-check/kulicnik-animation/kulicnik-bet.webm`;
});

const getFallbackIcon = computed(() => {
    if (props.isInStorybook) return `/images/img/lottery-settings/${props.lottery}/${props.lottery}-ico.png`;
    return `/frontend/web/img/lotteries/lottery-settings/${props.lottery}/${props.lottery}-ico.png`;
});

const getFallbackDefaultIcon = computed(() => {
    if (props.isInStorybook) return `/images/img/lottery-settings/default-ico.png`;
    return `/frontend/web/img/lotteries/lottery-settings/default-ico.png`;
});
</script>
